import classNames from 'classnames';
import { useState, useEffect, useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import Icon from '@utilities/icon';
import useModal from '@hooks/useModal';
import { colorPallete } from '@configs/color-pallete';
import SortBottomsheet from '@com/shared/ocs/filter/sortBottomsheet';
import { removeLastModalAction } from '@redux/modal/modalActions';

interface SortOption {
  label: string;
  value: string;
}

interface SortFilterProps {
  title?: string;
  paramKey: string;
  options: SortOption[];
  defaultLabel?: string;
  buttonClassNames?: string;
  disablePageNumberQuerySet?: boolean;
}

const SortFilter = ({
  title,
  options,
  paramKey,
  defaultLabel,
  buttonClassNames,
  disablePageNumberQuerySet = false,
}: SortFilterProps) => {
  const { addModal } = useModal();
  const pathname = usePathname();
  const router = useRouter();
  const params = useSearchParams();

  const [value, setValue] = useState<string | undefined>();

  useEffect(() => {
    setValue(params.get(paramKey) || undefined);
  }, [params, paramKey]);

  const applySort = useCallback(
    (nextValue?: string) => {
      const searchParams = new URLSearchParams(params.toString());

      if (nextValue) {
        searchParams.set(paramKey, nextValue);
      } else {
        searchParams.delete(paramKey);
      }

      if (!disablePageNumberQuerySet) {
        searchParams.set('pageNumber', '1');
      }

      router.push(`${pathname}?${searchParams.toString()}`);
      removeLastModalAction();
    },
    [params, pathname, router, paramKey],
  );

  const clear = () => {
    applySort(undefined);
    setValue(undefined);
  };

  const hasActive = !!value;

  const selectedLabel =
    options.find((o) => o.value === value)?.label || defaultLabel || title;

  return (
    <>
      <div
        onClick={() =>
          addModal({
            modal: SortBottomsheet,
            props: {
              title,
              paramKey,
              options,
              disablePageNumberQuerySet,
            },
          })
        }
        className={classNames(
          buttonClassNames,
          'relative flex text-Label/Small border items-center gap-x-1.5 rounded-full px-2 py-1 h-8 select-none cursor-pointer',
          {
            'border border-content-primary bg-surface-secondary': hasActive,
          },
        )}
      >
        <Icon
          name="LineThree"
          width={1}
          height={1}
          fill={colorPallete.content.primary}
        />

        <span>{selectedLabel}</span>

        {hasActive && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            className="flex items-center justify-center cursor-pointer"
          >
            <Icon name="Cross" width={1} height={1} />
          </span>
        )}
      </div>
    </>
  );
};

export default SortFilter;
